import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Button, Progress, Select, Table, TextInput } from '@mantine/core'
import { bulkUpdateDevices } from './server-fns'
import type { DeviceView } from '../api/generated/schemas'
import { Panel, StatusBadge, kickerCls, tdCls, thCls } from './ui'

/* Fleet/firmware/config pages derive their views from the device list —
   the API models these only as fields on a device. Sites have their own
   CRUD API and page. */

export const SAMPLE_LIMIT = 200

export function groupDevices(
  devices: DeviceView[],
  key: (d: DeviceView) => string | null | undefined,
) {
  const groups = new Map<string, DeviceView[]>()
  for (const d of devices) {
    const k = key(d) ?? 'unassigned'
    const list = groups.get(k)
    if (list) list.push(d)
    else groups.set(k, [d])
  }
  return [...groups.entries()].sort((a, b) => b[1].length - a[1].length)
}

export function SampleNote({ total, sampled }: { total: number; sampled: number }) {
  if (total <= sampled) return null
  return (
    <p className="mb-4 mt-0 font-mono text-xs text-slate-400 dark:text-slate-500">
      Grouped from the first {sampled} of {total} devices.
    </p>
  )
}

export function GroupTable({
  label,
  groups,
  linkParam,
}: {
  label: string
  groups: Array<[string, DeviceView[]]>
  linkParam: 'fleetId' | 'siteId'
}) {
  if (groups.length === 0) {
    return (
      <Panel className="px-4 py-12 text-center">
        <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
          No devices yet — every {label.toLowerCase()} view starts with a device.{' '}
          <Link
            to="/admin/devices"
            className="font-semibold text-blue-700 no-underline hover:underline dark:text-blue-400"
          >
            Add a device →
          </Link>
        </p>
      </Panel>
    )
  }

  return (
    <Panel>
      <Table.ScrollContainer minWidth={640}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={thCls}>{label}</Table.Th>
              <Table.Th className={thCls}>Devices</Table.Th>
              <Table.Th className={thCls}>Health</Table.Th>
              <Table.Th className={thCls} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groups.map(([id, devices]) => {
              const statuses = groupDevices(devices, (d) => d.status)
              return (
                <Table.Tr key={id}>
                  <Table.Td className={`${tdCls} font-mono text-xs`}>
                    {id === 'unassigned' ? (
                      <span className="text-slate-400 dark:text-slate-500">unassigned</span>
                    ) : (
                      id
                    )}
                  </Table.Td>
                  <Table.Td className={`${tdCls} font-mono text-xs`}>{devices.length}</Table.Td>
                  <Table.Td className={tdCls}>
                    <span className="flex flex-wrap gap-3">
                      {statuses.map(([status, list]) => (
                        <span key={status} className="inline-flex items-center gap-1">
                          <StatusBadge status={status} />
                          <span className="font-mono text-xs text-slate-400 dark:text-slate-500">
                            ×{list.length}
                          </span>
                        </span>
                      ))}
                    </span>
                  </Table.Td>
                  <Table.Td className={`${tdCls} text-right`}>
                    {id !== 'unassigned' ? (
                      <Link
                        to="/admin/devices"
                        search={{ [linkParam]: id }}
                        className="whitespace-nowrap text-xs font-semibold text-blue-700 no-underline hover:underline dark:text-blue-400"
                      >
                        View devices →
                      </Link>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Panel>
  )
}

export function BulkAssignPanel({
  target,
  onDone,
}: {
  target: 'fleetId' | 'siteId'
  onDone: () => void
}) {
  const noun = target === 'fleetId' ? 'fleet' : 'site'
  const [filterStatus, setFilterStatus] = useState<string | null>(null)
  const [filterFleet, setFilterFleet] = useState('')
  const [filterSite, setFilterSite] = useState('')
  const [targetId, setTargetId] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleAssign = async () => {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      const res = await bulkUpdateDevices({
        data: {
          filter: {
            ...(filterStatus ? { status: filterStatus } : {}),
            ...(filterFleet ? { fleetId: filterFleet } : {}),
            ...(filterSite ? { siteId: filterSite } : {}),
          },
          update: { [target]: targetId || null },
        },
      })
      setResult(
        `${res.updated} device${res.updated === 1 ? '' : 's'} moved to ${
          targetId ? `${noun} ${targetId}` : `no ${noun}`
        }.`,
      )
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk update failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel className="p-5">
      <p className={`${kickerCls} mb-1`}>Bulk assign</p>
      <p className="m-0 mb-4 text-sm text-slate-600 dark:text-slate-400">
        Move every device matching the filter into a {noun}. Leave the target empty to clear the
        assignment.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          label="Filter: status"
          placeholder="Any status"
          data={['active', 'provisioning', 'suspended', 'revoked', 'offline']}
          value={filterStatus}
          onChange={setFilterStatus}
          clearable
        />
        <TextInput
          label="Filter: fleet ID"
          value={filterFleet}
          onChange={(e) => setFilterFleet(e.currentTarget.value)}
        />
        <TextInput
          label="Filter: site ID"
          value={filterSite}
          onChange={(e) => setFilterSite(e.currentTarget.value)}
        />
        <TextInput
          label={`Target ${noun} ID`}
          value={targetId}
          onChange={(e) => setTargetId(e.currentTarget.value)}
        />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button
          disabled={!filterStatus && !filterFleet && !filterSite}
          loading={busy}
          onClick={() => void handleAssign()}
        >
          Assign to {noun}
        </Button>
        {result ? (
          <span className="font-mono text-xs text-emerald-600 dark:text-emerald-400">
            {result}
          </span>
        ) : null}
        {error ? (
          <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
        ) : null}
      </div>
    </Panel>
  )
}

export function VersionDistribution({
  label,
  groups,
  total,
}: {
  label: string
  groups: Array<[string, DeviceView[]]>
  total: number
}) {
  if (groups.length === 0 || total === 0) {
    return (
      <Panel className="px-4 py-12 text-center">
        <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
          No devices reporting a {label.toLowerCase()} yet.
        </p>
      </Panel>
    )
  }

  return (
    <Panel className="p-5">
      <div className="space-y-4">
        {groups.map(([version, devices]) => {
          const pct = Math.round((devices.length / total) * 100)
          return (
            <div key={version}>
              <div className="mb-1.5 flex items-baseline justify-between gap-4">
                <span className="font-mono text-xs text-slate-800 dark:text-slate-200">
                  {version === 'unassigned' ? (
                    <span className="text-slate-400 dark:text-slate-500">not reported</span>
                  ) : (
                    version
                  )}
                </span>
                <span className="font-mono text-xs text-slate-400 dark:text-slate-500">
                  {devices.length} · {pct}%
                </span>
              </div>
              <Progress value={Math.max(2, pct)} size="sm" radius="xl" />
            </div>
          )
        })}
      </div>
    </Panel>
  )
}
